import { type ExpressionSpecification } from 'maplibre-gl';
import GTM_HANDLES from '#/shared/gtm-handles';
import { type UniqueRadioButtonValues } from '#/types/inputs';

export const BIKETOWN_COMPANY_1 = 'BIKETOWN';
export const BIKETOWN_COMPANY_2 = 'GBFS';

export const BUS_LAYER = 'BUS_LAYER';
export const MAX_LAYER = 'MAX_LAYER';
export const WES_LAYER = 'WES_LAYER';
export const PSC_LAYER = 'PSC_LAYER';

// Map tile layers
export const TRIMET_STREETS_LAYER = 'trimet';
export const TRIMET_3D_LAYER = 'trimet-3d';
export const TRIMET_DARK_LAYER = 'trimet-dark';
export const TRIMET_DARK_3D_LAYER = 'trimet-dark-3d';
export const TRIMET_SATELLITE_LAYER = 'trimet-satellite';
export const TRIMET_SATELLITE_ROUTES_LAYER = 'trimet-satellite-routes';

export const MAP_LAYER_OPTIONS: UniqueRadioButtonValues[] = [
  {
    label: { stringId: 'map.streetslayer' },
    value: TRIMET_STREETS_LAYER,
  },
  {
    label: { stringId: 'map.aerialslayer' },
    value: TRIMET_SATELLITE_LAYER,
  },
].map(layerOption => ({
  ...layerOption,
  dataGtm: GTM_HANDLES.layerButton.mapTiles,
}));

// These keys are in CONSTANT_CASE, but our convention for localStorage
// keys is camelCase. We’ve kept them as-is to preserve existing user
// settings, but new keys should use camelCase
export const REALTIME_VEHICLES_TOGGLED_KEY = 'REALTIME_VEHICLES_TOGGLED';
export const SHOW_EXTRA_MAP_CONTROLS_ON_MOBILE_KEY = 'ALWAYS_SHOW_MAP_CONTROLS';

export const DUMMY_LNG = -122.68281340599061;
export const DUMMY_LAT = 45.52218740036735;

export const DEFAULT_ROUTE_COLOR = '#136390';

// Night bus routes to suppress from main map display.
export const NIGHT_BUS_ROUTE_IDS = ['287', '288', '291', '292', '293'];

export const VEHICLE_ICON_BASE_PX = 23;

// Route line widths (in px) by zoom level. Format is [zoom, thickness]
export const ROUTE_LINE_WIDTH_BY_ZOOM: [number, number][] = [
  [8, 2],
  [10, 4],
  [15, 6.5],
  [19, 20],
];

// Tracked vehicle icon sizes by zoom level. Format is [zoom, thickness]
export const TRACKED_VEHICLE_ICON_PIXEL_SIZE_BY_ZOOM: [number, number][] = [
  [8, 4],
  [10, 8],
  [16, 22.5],
  [19, 60],
];

/** Width for flex zone outlines */
export const FLEX_ZONE_OUTLINE_WIDTH = 2;

/** Multiplier for contrast outline width relative to the base outline width */
export const OUTLINE_WIDTH_MULTIPLIER = 1.8;

/** Multiplier for selected route contrast outline relative to focused route width */
const FOCUSED_OUTLINE_WIDTH_MULTIPLIER = 1.4;

/** Shared line-width for focused route constrast outlines */
export const FOCUSED_ROUTE_OUTLINE_WIDTH: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  ...ROUTE_LINE_WIDTH_BY_ZOOM.flatMap(([zoom, width]) => [
    zoom,
    width * FOCUSED_OUTLINE_WIDTH_MULTIPLIER,
  ]),
];

/** Shared layout for all route line layers */
export const ROUTE_LINE_LAYOUT = {
  'line-cap': 'round',
  'line-join': 'round',
} as const;

/**
 * Focused route line width expression (interpolated by zoom).
 * Matches ROUTE_LINE_WIDTH_BY_ZOOM steps.
 */
export const FOCUSED_ROUTE_WIDTH: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  ...ROUTE_LINE_WIDTH_BY_ZOOM.flat(),
];

// Default vehicle icon sizes by zoom level. Format is [zoom, size]
export const DEFAULT_VEHICLE_ICON_SIZE_BY_ZOOM: [number, number][] = [
  [9, 0.1],
  [10, 0.19],
  [11, 0.26],
  [12, 0.31],
  [13, 0.41],
  [14, 0.45],
  [15, 0.48],
  [16, 0.75],
  [17, 1.3],
  [18, 1.6],
  [19, 1.9],
  [20, 2.17],
];

export const TILE_URLS = {
  RTP_TILEJSON:
    'https://ws.trimet.org/rtp/routers/default/vectorTiles/rentalVehicles,rentalStations/tilejson.json',
  // TODO: PRJ77-822 - Follow up to see if this data is deemed accurate or will change following ticket work
  OTP_TILEJSON:
    'https://maps.trimet.org/rtp/routers/default/vectorTiles/rentalVehicles,realtimeRentalStations/tilejson.json',
  TRANSIT_PBF:
    'https://ws.trimet.org/geoserver/gwc/service/tms/1.0.0/ott:current@EPSG:900913@pbf/{z}/{x}/{y}.pbf',
} as const;

export const GEOJSON_URLS = {
  PARK_AND_RIDE: 'https://ws.trimet.org/rtp/routers/default/park_and_ride',
} as const;

// Unique identifiers for each map layer
// Used to reference specific layers in MapLibre GL JS
export const LAYER_IDS = {
  // Rental/mode overlays
  FREE_FLOATING_SCOOTERS: 'free-floating-scooters-layer',
  FREE_FLOATING_BICYCLES: 'free-floating-bicycles-layer',
  BICYCLE_STATIONS: 'bicycle-stations-layer',
  PARK_AND_RIDE: 'park-and-ride-layer',
  PARK_AND_RIDE_SYMBOL: 'park-and-ride-layer-symbol',

  // Routes
  CURRENT_ROUTES: 'current-routes-layer',
  CURRENT_ROUTES_OUTLINE: 'current-routes-layer-outline',
  CURRENT_ROUTES_SELECTED: 'current-routes-layer-selected',
  CURRENT_ROUTES_SELECTED_OUTLINE: 'current-routes-layer-selected-outline',

  // Flex zones
  FLEX_ZONES: 'flex-zones-layer',
  FLEX_ZONES_OUTLINE: 'flex-zones-outline-layer',
  FLEX_ZONES_OUTLINE_CONTRAST: 'flex-zones-outline-contrast-layer',

  // Stops
  STOPS: 'stops-layer',
  FAVORITE_STOPS: 'favorite-stop',

  // Realtime vehicles
  REAL_TIME_VEHICLES_3D_BODY: 'realtime-vehicles-layer-3d-body',
  REAL_TIME_VEHICLES_3D_DIVIDER: 'realtime-vehicles-layer-3d-divider',
  REAL_TIME_VEHICLES_3D_FRONT: 'realtime-vehicles-layer-3d-front',
  REAL_TIME_VEHICLES: 'realtime-vehicles-layer',
  REAL_TIME_VEHICLES_HOVER: 'realtime-vehicles-layer-hover',

  // Tracked vehicle (single vehicle view)
  TRACKED_VEHICLE: 'tracked-vehicle-layer',
  TRACKED_VEHICLE_REMAINING_OUTLINE: 'tracked-vehicle-layer-remaining-outline',
  TRACKED_VEHICLE_REMAINING: 'tracked-vehicle-layer-remaining',
  TRACKED_VEHICLE_TRAVELED: 'tracked-vehicle-layer-traveled',
  TRACKED_VEHICLE_3D_BODY: 'tracked-vehicle-layer-3d-body',
  TRACKED_VEHICLE_3D_DIVIDER: 'tracked-vehicle-layer-3d-divider',
  TRACKED_VEHICLE_3D_FRONT: 'tracked-vehicle-layer-3d-front',
  TRACKED_VEHICLE_VEHICLE: 'tracked-vehicle-layer-vehicle',

  // Labels (from base style or transitive overlay)
  ROUTES_LABELS: 'routes-labels',
  ROAD_MAJOR_LABEL: 'road_major_label',
} as const;

// Source layer names from the vector tile service
// These must match the layer names defined in the tile server
export const SOURCE_LAYERS = {
  FREE_FLOATING_SCOOTERS: 'rentalVehicles', // free-floating scooters
  FREE_FLOATING_BICYCLES: 'rentalVehicles', // free-floating bicycles
  BICYCLE_STATIONS: 'realtimeRentalStations', // bicycle stations
  CURRENT_ROUTES: 'current_routes', // current routes
  CURRENT_STOPS: 'current_stops', // current stops
  CURRENT_FLEX: 'current_flex', // current flex
} as const;

// Unique identifiers for data sources
// Used to reference specific sources in MapLibre GL JS
export const SOURCE_IDS = {
  OTP_VECTOR: 'otp-vector-source',
  PARK_AND_RIDE_GEOJSON: 'park-and-ride-geojson-source',
  TRANSIT_VECTOR: 'transit-vector-source',
  REAL_TIME_VEHICLES: 'realtime-vehicles-source',
  TRACKED_VEHICLE: 'tracked-vehicle-source',
} as const;

export const REFRESH_INTERVALS = {
  NEVER: null,
  DEFERRED: 0,
  ONE_SECOND: 1000,
  FIVE_SECONDS: 5000,
  TEN_SECONDS: 10000,
  THIRTY_SECONDS: 30000,
  ONE_MINUTE: 60000,
} as const;

// Shared list of all interactive layer IDs that can receive mouse events
export const INTERACTIVE_LAYER_IDS = [
  LAYER_IDS.FAVORITE_STOPS,
  LAYER_IDS.STOPS,
  LAYER_IDS.REAL_TIME_VEHICLES,
  LAYER_IDS.REAL_TIME_VEHICLES_3D_BODY,
  LAYER_IDS.REAL_TIME_VEHICLES_3D_DIVIDER,
  LAYER_IDS.REAL_TIME_VEHICLES_3D_FRONT,
  LAYER_IDS.PARK_AND_RIDE,
  LAYER_IDS.PARK_AND_RIDE_SYMBOL,
  LAYER_IDS.FREE_FLOATING_BICYCLES,
  LAYER_IDS.BICYCLE_STATIONS,
  LAYER_IDS.FREE_FLOATING_SCOOTERS,
] as const;

/**
 * LAYER STACKING ORDER (bottom to top)
 *
 * Defines the visual z-order hierarchy of all map layers.
 * Layers earlier in the array render BELOW layers later in the array.
 *
 * Visual hierarchy (bottom → top):
 *   map base tiles
 *   → flex zones
 *   → route lines (lowest interactive layer - all elements render above routes)
 *   → map labels
 *   → mode overlays (rental vehicles, park & ride)
 *   → real-time vehicle icons
 *   → stop markers (including favorites)
 *   → itinerary elements (React overlays, not MapLibre layers)
 *   → current location marker (React component, not MapLibre layer)
 *   → tooltips/popups (React components, always on top)
 *
 * When adding a new layer, insert it at the appropriate position.
 *
 * Note: Tooltips, context menus, and itinerary overlays are React components
 * rendered outside MapLibre's canvas, so they naturally appear above all map layers.
 *
 * Reference from design spec (NON-ITINERARY, bottom to top):
 *   - map base tiles
 *   - route lines (changed from original spec to be lowest)
 *   - map labels
 *   - mode overlays (BIKETOWN, Lime, park & ride)
 *   - real-time vehicle icons
 *   - stop markers, plus favorite, plus tooltip
 *   - origin/destination/address markers (React markers)
 *   - current location marker (React marker)
 *   - vehicle tooltip (React component)
 *   - contextmenu tooltip (React component)
 *   - current location tooltip (React component)
 */
export const LAYER_Z_ORDER = [
  // === FLEX ZONES (lowest, below everything) ===
  LAYER_IDS.FLEX_ZONES,
  LAYER_IDS.FLEX_ZONES_OUTLINE_CONTRAST,
  LAYER_IDS.FLEX_ZONES_OUTLINE,

  // === ROUTE LINES (above flex, below all interactive features) ===
  // Routes render at the lowest level so vehicles, stops, and mode overlays are always visible
  LAYER_IDS.CURRENT_ROUTES_OUTLINE,
  LAYER_IDS.CURRENT_ROUTES,
  LAYER_IDS.TRACKED_VEHICLE_REMAINING_OUTLINE,
  LAYER_IDS.TRACKED_VEHICLE_REMAINING,
  LAYER_IDS.TRACKED_VEHICLE_TRAVELED,

  // === MAP LABELS (above routes, below interactive features) ===
  LAYER_IDS.ROUTES_LABELS,
  // Selected route lines sit above route labels but below road labels.
  LAYER_IDS.CURRENT_ROUTES_SELECTED_OUTLINE,
  LAYER_IDS.CURRENT_ROUTES_SELECTED,
  LAYER_IDS.ROAD_MAJOR_LABEL,

  // === MODE OVERLAYS (above labels, interactive point features) ===
  // Order: Lime bikes/scooters (lowest), BIKETOWN (highest)
  LAYER_IDS.FREE_FLOATING_BICYCLES,
  LAYER_IDS.FREE_FLOATING_SCOOTERS,
  LAYER_IDS.BICYCLE_STATIONS,
  LAYER_IDS.PARK_AND_RIDE,
  LAYER_IDS.PARK_AND_RIDE_SYMBOL,

  // === REAL-TIME VEHICLES (above mode overlays, below stops) ===
  LAYER_IDS.REAL_TIME_VEHICLES_3D_BODY,
  LAYER_IDS.REAL_TIME_VEHICLES_3D_DIVIDER,
  LAYER_IDS.REAL_TIME_VEHICLES_3D_FRONT,
  LAYER_IDS.REAL_TIME_VEHICLES,
  LAYER_IDS.REAL_TIME_VEHICLES_HOVER,
  LAYER_IDS.TRACKED_VEHICLE_3D_BODY,
  LAYER_IDS.TRACKED_VEHICLE_3D_DIVIDER,
  LAYER_IDS.TRACKED_VEHICLE_3D_FRONT,
  LAYER_IDS.TRACKED_VEHICLE_VEHICLE,

  // === STOPS (highest MapLibre layer, always visible above everything) ===
  LAYER_IDS.STOPS,
  LAYER_IDS.FAVORITE_STOPS,
] as const;
